import React, { useEffect, useState } from "react";
import { Col, Row, Table } from "react-bootstrap";
import { api } from "../services/api";
import PageHeader from "./ui/PageHeader";
import DataCard from "./ui/DataCard";
import StatusBadge from "./ui/StatusBadge";
import LoadingState from "./ui/LoadingState";
import AlertBanner from "./ui/AlertBanner";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      setUsers(await api.getUsers());
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.mobile.includes(filter) ||
      user.email?.toLowerCase().includes(filter.toLowerCase()) ||
      user.kycStatus.toLowerCase().includes(filter.toLowerCase()) ||
      user.name.toLowerCase().includes(filter.toLowerCase())
  );

  const updateStatus = async (id, newStatus) => {
    setActionLoading(`${id}-${newStatus}`);
    setFeedback(null);
    try {
      const result = await api.updateUserStatus(id, newStatus.toLowerCase());
      setUsers((prev) =>
        prev.map((user) =>
          user.id === id
            ? { ...user, status: result.status, statusKey: result.statusKey, activity: `Status → ${result.status}` }
            : user
        )
      );
      setFeedback({ type: "success", message: `User #${id} is now ${result.status}.` });
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <LoadingState message="Loading users..." />;

  return (
    <div>
      <PageHeader title="User Management" subtitle="Manage customers, KYC status and account access" />

      {feedback && (
        <AlertBanner type={feedback.type} message={feedback.message} onClose={() => setFeedback(null)} />
      )}

      <Row className="mb-4">
        <Col md={5}>
          <input
            className="billr-input"
            placeholder="Search name, mobile, email or KYC..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      <DataCard title={`Customers (${filteredUsers.length})`}>
        <Table responsive className="billr-table mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact</th>
              <th>KYC</th>
              <th>Status</th>
              <th>Device</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-muted py-4">No users found</td></tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.name}</strong>
                    <div className="small text-muted">{user.activity}</div>
                  </td>
                  <td>
                    <div>{user.mobile}</div>
                    <div className="small text-muted">{user.email}</div>
                  </td>
                  <td><StatusBadge status={user.kycStatus.toLowerCase()} label={user.kycStatus} /></td>
                  <td><StatusBadge status={user.statusKey} label={user.status} /></td>
                  <td>{user.device}</td>
                  <td>
                    <div className="d-flex gap-1 flex-wrap">
                      <button
                        className="btn-billr btn-billr-outline btn-billr-sm"
                        disabled={user.statusKey === "suspended" || actionLoading === `${user.id}-suspended`}
                        onClick={() => updateStatus(user.id, "suspended")}
                      >
                        Suspend
                      </button>
                      <button
                        className="btn-billr btn-billr-primary btn-billr-sm"
                        disabled={user.statusKey === "active" || actionLoading === `${user.id}-active`}
                        onClick={() => updateStatus(user.id, "active")}
                      >
                        Activate
                      </button>
                      <button
                        className="btn-billr btn-billr-outline btn-billr-sm"
                        style={{ color: "#ef4444", borderColor: "#fecaca" }}
                        disabled={user.statusKey === "banned" || actionLoading === `${user.id}-banned`}
                        onClick={() => updateStatus(user.id, "banned")}
                      >
                        Ban
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </DataCard>
    </div>
  );
};

export default UserManagement;
